import { STORAGE_KEYS } from '@config/constants';

/**
 * Storage utility that handles localStorage/sessionStorage based on rememberMe flag
 * Matches Angular's ngx-webstorage behavior
 */
export class StorageService {
    /**
     * Get the rememberMe preference
     */
    static getRememberMe(): boolean {
        return localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
    }

    /**
     * Set the rememberMe preference
     */
    static setRememberMe(value: boolean): void {
        if (value) {
            localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
        } else {
            localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
        }
    }

    /**
     * Get subscriber ID from appropriate storage
     */
    static getSubscriberId(): number | null {
        const localValue = localStorage.getItem(STORAGE_KEYS.SUBSCRIBER_ID);
        const sessionValue = sessionStorage.getItem(STORAGE_KEYS.SUBSCRIBER_ID);
        const value = localValue || sessionValue;
        return value ? parseInt(value, 10) : null;
    }

    /**
     * Store subscriber ID in appropriate storage based on rememberMe
     */
    static storeSubscriberId(subscriberId: number, rememberMe?: boolean): void {
        const useLocal = rememberMe ?? this.getRememberMe();
        const value = subscriberId.toString();

        if (useLocal) {
            localStorage.setItem(STORAGE_KEYS.SUBSCRIBER_ID, value);
            sessionStorage.removeItem(STORAGE_KEYS.SUBSCRIBER_ID);
        } else {
            sessionStorage.setItem(STORAGE_KEYS.SUBSCRIBER_ID, value);
            localStorage.removeItem(STORAGE_KEYS.SUBSCRIBER_ID);
        }
    }

    /**
     * Update subscriber ID in whichever storage it's currently in
     */
    static updateSubscriberId(subscriberId: number): void {
        const value = subscriberId.toString();

        if (localStorage.getItem(STORAGE_KEYS.SUBSCRIBER_ID)) {
            localStorage.setItem(STORAGE_KEYS.SUBSCRIBER_ID, value);
        }
        if (sessionStorage.getItem(STORAGE_KEYS.SUBSCRIBER_ID)) {
            sessionStorage.setItem(STORAGE_KEYS.SUBSCRIBER_ID, value);
        }
    }

    /**
     * Clear subscriber ID from both storages
     */
    static clearSubscriberId(): void {
        localStorage.removeItem(STORAGE_KEYS.SUBSCRIBER_ID);
        sessionStorage.removeItem(STORAGE_KEYS.SUBSCRIBER_ID);
    }

    /**
     * Get impersonation username
     */
    static getImpersonation(): string | null {
        return localStorage.getItem(STORAGE_KEYS.IMPERSONATE_USERNAME);
    }

    /**
     * Set impersonation username
     */
    static setImpersonation(username: string | null): void {
        if (username) {
            localStorage.setItem(STORAGE_KEYS.IMPERSONATE_USERNAME, username);
        } else {
            localStorage.removeItem(STORAGE_KEYS.IMPERSONATE_USERNAME);
        }
    }

    /**
     * Clear all auth-related storage
     */
    static clearAll(): void {
        localStorage.removeItem(STORAGE_KEYS.SUBSCRIBER_ID);
        localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
        localStorage.removeItem(STORAGE_KEYS.IMPERSONATE_USERNAME);
        sessionStorage.removeItem(STORAGE_KEYS.SUBSCRIBER_ID);
    }
}

export default StorageService;
